import { Paper, Stack, Typography } from "@mui/material";
import { PlanGenerationAttemptOut } from "@providers/hop-ord-server/api";
import dayjs from "dayjs";
import { COLOUR_BLIND_COLOURS } from "./MetricsTable";
import { DonutChart, LegendItem } from "./MetricsSummaryComponents";
import { useMemo } from "react";

interface MetricsSummaryProps {
  rows: PlanGenerationAttemptOut[];
}

const MetricsSummary = ({ rows }: MetricsSummaryProps) => {
  const {
    totalPlans,
    inProgressPlans,
    successfulPlans,
    failedPlans,
    avgMin,
    avgRemSec,
  } = useMemo(() => {
    const totalPlans = rows.length;

    let inProgressPlans = 0;
    let successfulPlans = 0;
    let failedPlans = 0;
    let totalDurationSec = 0;
    let durationCount = 0;

    for (const r of rows) {
      if (!r.finishedAt) {
        inProgressPlans += 1;
      } else {
        if (r.success) {
          successfulPlans += 1;
        } else {
          failedPlans += 1;
        }
        if (r.planningStartedAt && r.finishedAt) {
          const seconds = dayjs(r.finishedAt).diff(
            dayjs(r.planningStartedAt),
            "second",
          );
          if (Number.isFinite(seconds) && seconds >= 0) {
            totalDurationSec += seconds;
            durationCount += 1;
          }
        }
      }
    }

    const avgSec = durationCount
      ? Math.round(totalDurationSec / durationCount)
      : 0;
    const avgMin = Math.floor(avgSec / 60);
    const avgRemSec = avgSec % 60;

    return {
      totalPlans,
      inProgressPlans,
      successfulPlans,
      failedPlans,
      avgMin,
      avgRemSec,
    };
  }, [rows]);

  return (
    <Paper sx={{ p: 2, width: 320 }}>
      <Stack alignItems="center" spacing={1.5} direction="row">
        <DonutChart
          total={totalPlans}
          inProgress={inProgressPlans}
          success={successfulPlans}
          failed={failedPlans}
        />
        <Stack spacing={2}>
          <LegendItem
            color={COLOUR_BLIND_COLOURS.inProgress}
            label="In Progress"
            value={inProgressPlans}
          />
          <LegendItem
            color={COLOUR_BLIND_COLOURS.success}
            label="Success"
            value={successfulPlans}
          />
          <LegendItem
            color={COLOUR_BLIND_COLOURS.error}
            label="Failed"
            value={failedPlans}
          />
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            <Typography variant="body2">Average duration:</Typography>
            <Typography variant="body2">
              {avgMin}m {String(avgRemSec).padStart(2, "0")}s
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default MetricsSummary;
